import { Loader2 } from "lucide-react";
import { useCandidateProjects } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { ProjectDetail } from "@/interface/project";
import { formatDate } from "@/utils/helper";

export function ProjectsTab({
  profileId,
  projects = [],
}: {
  profileId?: number | string;
  projects?: ProjectDetail[];
}) {
  const {
    data: projectsQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateProjects(profileId, { page: 1, page_size: 10 });

  const queryProjects = projectsQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const projectList: ProjectDetail[] =
    queryProjects && queryProjects.length > 0
      ? queryProjects
      : Array.isArray(projects)
        ? projects
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  const getTechTags = (proj: ProjectDetail): string[] => {
    const rawTech = proj.technologies;

    if (Array.isArray(rawTech)) {
      return rawTech.map((t) =>
        typeof t === "object" && t !== null
          ? t.skill_name || String(t)
          : String(t),
      );
    }
    if (typeof rawTech === "string" && (rawTech as string).trim()) {
      return (rawTech as string)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    return [];
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Project History
      </h2>

      {isLoading && projectList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {projectList.length === 0 ? (
            <div className="text-center p-8 text-slate-500 text-sm">
              No projects listed.
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 border-b border-slate-200 dark:border-slate-800">
                  <tr>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      Project Name
                    </th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      Client / Organization
                    </th>
                    <th className="px-4 py-3 font-semibold whitespace-nowrap">
                      Role & Duration
                    </th>
                    <th className="px-4 py-3 font-semibold">Technologies</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                  {projectList.map((row: ProjectDetail, i: number) => {
                    const name = row.project_name || "Project";
                    const org = row.client_organization || "N/A";
                    const role = row.role || "Contributor";

                    const startDate = row.start_date
                      ? formatDate(row.start_date, "DD-MM-YYYY")
                      : "";
                    const endDate = row.end_date
                      ? formatDate(row.end_date, "DD-MM-YYYY")
                      : "Present";
                    const duration =
                      startDate || endDate
                        ? `${startDate}${startDate && endDate ? " - " : ""}${endDate}`
                        : "N/A";

                    const tags = getTechTags(row);

                    return (
                      <tr
                        key={row.id || i}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/30"
                      >
                        <td className="px-4 py-4">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {name}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-slate-600 dark:text-slate-300">
                          {org}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-slate-900 dark:text-white font-medium">
                            {role}
                          </div>
                          <div className="text-xs text-slate-500">{duration}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1.5 flex-wrap">
                            {tags.map((t: string, idx: number) => (
                              <span
                                key={idx}
                                className="px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 text-[10px] text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800"
                              >
                                {t}
                              </span>
                            ))}
                            {tags.length === 0 && (
                              <span className="text-[10px] text-slate-400 italic">
                                -
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more projects...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
